import { PropsWithChildren, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import {
  formatTime,
  getHeadlineCategoryById,
  headlinePublicUrl,
} from "./utils";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { Button } from "@/ui-components/button-with-loading";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/solid";
import {
  DocumentTextIcon,
  ArrowTrendingUpIcon,
} from "@heroicons/react/20/solid";

import Markdown, { type Components } from "react-markdown";
import { useFontStore } from "@/stores/store";
import { HighlightInfo } from "@/utils/portfolioUtils";
import {
  highlightTermsInText,
  SEARCH_HIGHLIGHT_COLOR,
} from "@/utils/text-highlight";
import { Headline } from "./models/Headline";
import { safeClipboardWrite } from "@/utils/clipboard";
import { useHasInternalNewsquawkUserRole } from "@/hooks/useHasInternalNewsquawkUserRole";
import rehypeRaw from "rehype-raw";
import { BgOverlay } from "../firehose-widgets/firehose-all/components";
import {
  checkableTagIds,
  checkableTags,
} from "../create-headline/models/HeadlineForm";
import { ShowMoreWrapper } from "@/ui-components/show-more-wrapper";
import { Tag, useTagManagerHydrate } from "@/shared/api/newsquawk-headlines";
import { useBrokerStore } from "@/stores/BrokerStore";
import {
  useSettingsStore,
  importantHeadlineColorPresets,
} from "@/stores/SettingsStore";
import { trackContentView, trackContentEngagement } from "@/utils/gtm";

type NewsquawkHeadlineItemProps = {
  headline: Headline;
  onItemEdit?: (headline: Headline) => void;
  onItemDelete?: (headline: Headline) => void;
  highlightInfo?: HighlightInfo;
  searchQuery?: string;
};

const itemStyles: Record<number, string> = {
  [checkableTagIds.Flash]: "bg-terminal-flash text-black",
  [checkableTagIds.Research]:
    "bg-terminal-green text-black dark:text-gray-300 bg-opacity-50",
  [checkableTagIds.Highlighted]:
    "bg-terminal-green text-black dark:text-gray-300 bg-opacity-50",
};

const getItemStyle = (tagIds: number[]) => {
  const tag = checkableTags.find(
    (tag) => tagIds.includes(tag.id) && tag.id !== checkableTagIds.Important
  );
  return tag ? itemStyles[tag.id] : undefined;
};

const isImportantHeadline = (tagIds: number[]) =>
  tagIds.includes(checkableTagIds.Important);

// Tags to display as chips
const getDisplayTags = (tags: Tag[]): Tag[] => tags;

function NewsquawkHeadlineItem(props: NewsquawkHeadlineItemProps) {
  const { headline } = props;

  const [showDetails, setShowDetails] = useState(
    headline.show_details ?? false
  );

  // Sync local state when the headline changes (virtuoso reuses component instances)
  useEffect(() => {
    setShowDetails(headline.show_details ?? false);
  }, [headline.id, headline.show_details]);

  const toggleHeadlineItemClicked = useFontStore(
    // @ts-expect-error - Store type definition is missing
    (state) => state.toggleNewsquawkHeadlineFont
  );

  const isClicked = useFontStore(
    // @ts-expect-error - Store type definition is missing
    (state) => state.clickedNewsquawkHeadlines[headline.id] ?? false
  );

  const internalNewsquawkUser = useHasInternalNewsquawkUserRole();

  const headlineCategory = useMemo(() => {
    for (const tagId of headline.tag_id_list) {
      const category = getHeadlineCategoryById(Number(tagId));

      if (category) {
        return category.title;
      }
    }

    return undefined;
  }, [headline.tag_id_list]);

  const importantHeadlineColor = useSettingsStore(
    (state) => state.importantHeadlineColor
  );

  const isImportant = useMemo(
    () => isImportantHeadline(headline.tag_id_list),
    [headline.tag_id_list]
  );

  const importantStyle = useMemo(() => {
    if (!isImportant) return undefined;
    const preset = importantHeadlineColorPresets[importantHeadlineColor];
    return { backgroundColor: preset.bg, color: preset.text };
  }, [isImportant, importantHeadlineColor]);

  const itemStyle = useMemo(
    () => getItemStyle(headline.tag_id_list),
    [headline.tag_id_list]
  );

  // Headline does not contain any of the checkable tags
  const isOtherHeadline = !isImportant && !itemStyle;

  // Track when details were opened for engagement time measurement
  const openedAtRef = useRef<number | null>(null);

  // Broker quick-trade (beta): enabled in Settings → Broker; requires a
  // connected terminal that's authorised to trade (demo, or live opted-in).
  const quickTradeEnabled = useBrokerStore(
    (s) => s.quickTrade.enabled && !!s.account?.connected && !s.account?.live_blocked && !s.account?.kill_switch
  );
  const quickTradeLots = useBrokerStore((s) => s.quickTrade.lots);
  const symbolForTags = useBrokerStore((s) => s.symbolForTags);
  const quickOrder = useBrokerStore((s) => s.quickOrder);
  const quickTradeSymbol = useMemo(
    // strongOnly: a bare currency tag on (say) an equities story shouldn't
    // grow FX trade buttons — only instrument-grade tags qualify.
    () => (quickTradeEnabled ? symbolForTags(headline.tags, true) : null),
    [quickTradeEnabled, symbolForTags, headline.tags]
  );

  const toggleDetails = (item: Headline) => {
    if (!isClicked) toggleHeadlineItemClicked(item.id);

    // Broker widgets in "on click"/"follow feed" auto-switch mode load the
    // asset this headline's tags map to (no-op when the mode is off).
    useBrokerStore.getState().publishTags("click", item.tags);

    if (!showDetails) {
      // Opening details — track content view and record open time
      openedAtRef.current = Date.now();
      trackContentView({
        content_id: item.id,
        content_title: item.subject,
        content_type: "headline",
        content_category: headlineCategory ?? "uncategorized",
        content_source: item.source,
        content_tags: item.tags
          ?.map((t) => t.name)
          .filter(Boolean)
          .join(", "),
        content_widget: "headlines",
      });
    } else if (openedAtRef.current) {
      // Closing details — track engagement time
      trackContentEngagement({
        content_id: item.id,
        content_title: item.subject,
        content_type: "headline",
        content_widget: "headlines",
        engagement_time_ms: Date.now() - openedAtRef.current,
      });
      openedAtRef.current = null;
    }

    headline.show_details = !headline.show_details;

    setShowDetails((prev) => !prev);
  };

  return (
    <div>
      <button
        onClick={() => toggleDetails(props.headline)}
        className={clsx(
          "focus:outline-none cursor-pointer flex items-start text-xs gap-1 w-full rounded-none py-1 transition relative z-0 pr-4",
          isOtherHeadline
            ? "bg-transparent text-gray-900 dark:text-gray-100"
            : !isImportant && itemStyle,
          isClicked && "font-normal",
          isClicked && isOtherHeadline && "text-gray-500 dark:text-gray-400",
          !isClicked && "font-semibold",
          showDetails && "border-x border-t border-squawk-blue/40"
        )}
        style={importantStyle}
      >
        {/* Left border accent — blue for new, cyan for updated (30s hold + 5s fade) */}
        {headline.received_at && (
          <BgOverlay
            startTime={new Date(headline.received_at)}
            color={
              isImportant
                ? importantHeadlineColorPresets[importantHeadlineColor].text
                : headline.wasUpdated
                  ? "#eab308"
                  : "#00aeef"
            }
          />
        )}

        {/* Brief full-row flash on arrival (3s fade) */}
        {headline.received_at && Date.now() - headline.received_at < 3000 && (
          <div
            key={`flash-${headline.id}-${headline.received_at}`}
            className="absolute inset-0 z-10 pointer-events-none animate-flash-out"
            style={{
              animationDuration: "3s",
              "--flash": headline.wasUpdated
                ? "rgba(234, 179, 8, 0.4)"
                : isImportant
                  ? importantHeadlineColorPresets[importantHeadlineColor].bg
                  : itemStyle?.includes("terminal-green")
                    ? "rgba(0, 200, 83, 0.4)"
                    : "rgba(0, 174, 239, 0.4)",
            } as CSSProperties}
          />
        )}

        <span className="whitespace-nowrap px-1 text-xs w-10 text-left">
          {typeof headline.published_at === "object"
            ? formatTime(
                new Date(headline.published_at.unix * 1000).toISOString()
              )
            : new Date(headline.published_at as string).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
        </span>

        {internalNewsquawkUser && headlineCategory && (
          <span className="px-1 text-xs w-20">{headlineCategory}</span>
        )}

        <p className={"flex-1 px-1 text-start text-xs"}>
          {props.searchQuery?.trim() ? (
            <span
              dangerouslySetInnerHTML={{
                __html: highlightTermsInText(
                  headline.subject,
                  props.searchQuery.trim().split(/\s+/).filter(Boolean),
                  SEARCH_HIGHLIGHT_COLOR
                ),
              }}
            />
          ) : (
            <span
              style={{
                backgroundColor: props.highlightInfo?.color,
                color: props.highlightInfo && "black",
              }}
            >
              {headline.subject}
            </span>
          )}

          {props.highlightInfo && (
            <span
              style={{ color: props.highlightInfo.color }}
              className="text-xs"
            >
              {` (${props.highlightInfo.portfolioName})`}
            </span>
          )}
        </p>

        {/* Broker quick-trade chips (beta): one-click market orders with the
            default lot size set in Settings → Broker. Rendered only when the
            feature is on, a demo/authorised terminal is connected, and this
            headline's tags map to a symbol the broker trades. */}
        {quickTradeSymbol && (
          <span
            className="flex items-center gap-1 self-center pr-1"
            onClick={(e) => e.stopPropagation()}
          >
            <span
              role="button"
              title={`Sell ${quickTradeLots} lots ${quickTradeSymbol} at market`}
              onClick={(e) => {
                e.stopPropagation();
                quickOrder(quickTradeSymbol, "market_sell");
              }}
              className="rounded bg-red-500/15 px-1.5 py-0.5 text-1.5xs font-bold text-red-500 hover:bg-red-500/30 transition"
            >
              S·{quickTradeSymbol}
            </span>
            <span
              role="button"
              title={`Buy ${quickTradeLots} lots ${quickTradeSymbol} at market`}
              onClick={(e) => {
                e.stopPropagation();
                quickOrder(quickTradeSymbol, "market_buy");
              }}
              className="rounded bg-green-500/15 px-1.5 py-0.5 text-1.5xs font-bold text-green-500 hover:bg-green-500/30 transition"
            >
              B·{quickTradeSymbol}
            </span>
          </span>
        )}

        {(headline.content ||
          (headline.analysis_details as string) ||
          (headline.reaction_details as string)) && (
          <span className="flex items-center gap-0.5 p-0.5 text-sm bg-transparent shrink-0">
            {isValidDetail(headline.analysis_details as string) && (
              <DocumentTextIcon
                className="size-3.5"
                title="Has analysis details"
              />
            )}
            {isValidDetail(headline.reaction_details as string) && (
              <ArrowTrendingUpIcon
                className="size-3.5"
                title="Has market reaction"
              />
            )}
            {showDetails ? "↑" : "↓"}
          </span>
        )}
      </button>

      {showDetails && (
        <HeadlineDetails
          headline={headline}
          highlightInfo={props.highlightInfo}
          searchQuery={props.searchQuery}
        >
          {internalNewsquawkUser && (
            <div className="flex items-center justify-end gap-2 py-2">
              {props.onItemEdit && (
                <Button
                  size="md"
                  variant="secondary"
                  renderIcon={() => <PencilSquareIcon className="h-4 w-4" />}
                  onClick={() => props.onItemEdit?.(props.headline)}
                >
                  Edit
                </Button>
              )}
              {props.onItemDelete && (
                <button
                  onClick={() => props.onItemDelete?.(props.headline)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-md border border-red-600 bg-red-900/20 text-red-400 hover:border-red-500 hover:text-red-300 hover:bg-red-900/30 transition-colors"
                >
                  <TrashIcon className="size-4" />
                  Delete
                </button>
              )}
            </div>
          )}
        </HeadlineDetails>
      )}

      <div className="border-b border-gray-200 dark:border-gray-800" />
    </div>
  );
}

type HeadlineDetailsProps = {
  headline: Headline;
  highlightInfo?: HighlightInfo;
  searchQuery?: string;
} & PropsWithChildren;

const HeadlineDetails = ({
  headline,
  highlightInfo,
  searchQuery,
  children,
}: HeadlineDetailsProps) => {
  const [showCopied, setShowCopied] = useState<
    "headline" | "body" | "url" | null
  >(null);
  const internalNewsquawkUser = useHasInternalNewsquawkUserRole();

  // Get tags to display (excluding checkable category tags)
  const tagsToDisplay = useMemo(
    () => getDisplayTags(headline.tags || []),
    [headline.tags]
  );

  // Hydrate tags with missing names from cache/API
  const { tags: displayTags, isLoading: tagsLoading } = useTagManagerHydrate(
    tagsToDisplay.length > 0 ? tagsToDisplay : null
  );
  // Strip source attribution lines from content to avoid duplication with the Source section
  const deduplicatedContent = useMemo(
    () => stripSourceFromContent(headline.content, headline.source),
    [headline.content, headline.source]
  );
  const deduplicatedLinkedContent = useMemo(
    () =>
      stripSourceFromContent(
        headline.linked_content as string,
        headline.source
      ),
    [headline.linked_content, headline.source]
  );

  // Helper to apply highlighting to text content
  const applyHighlight = (text: string | null | undefined): string => {
    if (!text) return "";
    let result = text;
    if (highlightInfo?.matchedTickers?.length) {
      result = highlightTermsInText(
        result,
        highlightInfo.matchedTickers,
        highlightInfo.color
      );
    }
    if (searchQuery?.trim()) {
      const searchTerms = searchQuery.trim().split(/\s+/).filter(Boolean);
      if (searchTerms.length > 0) {
        result = highlightTermsInText(
          result,
          searchTerms,
          SEARCH_HIGHLIGHT_COLOR
        );
      }
    }
    return result;
  };

  const handleHeadlineCopy = () => {
    setShowCopied("headline");
    safeClipboardWrite(headline.subject);
  };

  const handleBodyCopy = () => {
    setShowCopied("body");

    const sections: string[] = [];

    if (isValidDetail(deduplicatedContent)) {
      sections.push(deduplicatedContent);
    }
    if (isValidDetail(deduplicatedLinkedContent)) {
      sections.push(deduplicatedLinkedContent);
    }
    if (isValidDetail((headline.analysis_details as string) ?? "")) {
      sections.push(headline.analysis_details as string);
    }
    if (isValidDetail(headline.reaction_details as string)) {
      sections.push(headline.reaction_details as string);
    }
    if (isValidDetail(headline.source)) {
      sections.push(headline.source);
    }

    safeClipboardWrite(sections.join("\n\n"));
  };

  const handleUrlCopy = () => {
    setShowCopied("url");
    // Slug is derived from the raw stored subject, not the displayed text.
    safeClipboardWrite(headlinePublicUrl(headline.subject));
  };

  useEffect(() => {
    if (showCopied) {
      const timer = setTimeout(() => setShowCopied(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [showCopied]);

  // Format timestamp for display
  const formatTimestamp = (
    timestamp: { text?: string; iso_8601?: string; unix?: number } | undefined
  ): string => {
    if (!timestamp) return "";
    if (timestamp.text) return timestamp.text;
    if (timestamp.iso_8601)
      return new Date(timestamp.iso_8601).toLocaleString();
    if (timestamp.unix) return new Date(timestamp.unix * 1000).toLocaleString();
    return "";
  };

  return (
    <>
      <div className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs space-y-2 p-4 transition-colors border-x border-b border-squawk-blue/40">
        <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-snug">
          {headline.subject}
        </h3>
        {isValidDetail(deduplicatedContent) && (
          <div className="prose dark:prose-invert prose-xs max-w-none">
            <Markdown
              skipHtml={false}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
            >
              {applyHighlight(deduplicatedContent)}
            </Markdown>
          </div>
        )}

        {isValidDetail(deduplicatedLinkedContent) && (
          <>
            <div className="prose dark:prose-invert prose-xs max-w-none">
              <Markdown
                skipHtml={false}
                rehypePlugins={[rehypeRaw]}
                components={markdownComponents}
              >
                {applyHighlight(deduplicatedLinkedContent)}
              </Markdown>
            </div>
          </>
        )}

        {isValidDetail((headline.analysis_details as string) ?? "") && (
          <div className="space-y-1">
            <strong className="text-gray-900 dark:text-gray-100">
              Analysis Details
            </strong>

            <div className="prose dark:prose-invert prose-xs max-w-none">
              <Markdown
                skipHtml={false}
                rehypePlugins={[rehypeRaw]}
                components={markdownComponents}
              >
                {applyHighlight(headline.analysis_details as string)}
              </Markdown>
            </div>

            {headline.analysis_updated_at &&
              formatTimestamp(headline.analysis_updated_at) && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Analysis last updated{" "}
                  {formatTimestamp(headline.analysis_updated_at)}
                </p>
              )}
          </div>
        )}

        {isValidDetail(headline.reaction_details as string) && (
          <div className="space-y-1 prose dark:prose-invert prose-xs max-w-none">
            <strong className="text-gray-900 dark:text-gray-100">
              Reaction Details
            </strong>{" "}
            <div className="prose dark:prose-invert prose-xs max-w-none">
              <Markdown
                skipHtml={false}
                rehypePlugins={[rehypeRaw]}
                components={markdownComponents}
              >
                {applyHighlight(headline.reaction_details as string)}
              </Markdown>
            </div>
            {headline.reaction_updated_at &&
              formatTimestamp(headline.reaction_updated_at) && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Reaction last updated{" "}
                  {formatTimestamp(headline.reaction_updated_at)}
                </p>
              )}
          </div>
        )}
        {isValidDetail(headline.source) && (
          <div className="space-y-1">
            <strong className="text-gray-900 dark:text-gray-100">
              Source{" "}
            </strong>
            <p
              dangerouslySetInnerHTML={{
                __html: ensureLinksOpenNewTab(applyHighlight(headline.source)),
              }}
            ></p>
          </div>
        )}
        {isValidDetail(headline.section as string) && (
          <div className="space-y-1">
            <strong className="text-gray-900 dark:text-gray-100">
              Section
            </strong>{" "}
            <span
              dangerouslySetInnerHTML={{
                __html: ensureLinksOpenNewTab(headline.section as string),
              }}
            ></span>
          </div>
        )}
        {/* {highlight && highlight.matchedTickers.length > 0 && (
                  <div>
                    <strong>Matched Tickers:</strong>{" "}
                    {highlight.matchedTickers.join(", ")}
                  </div>
                )} */}
        {/* Action buttons */}
        <div className="mt-4 flex items-center justify-end gap-3">
          <a
            href={createMailtoLink(headline.subject, headline.content)}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            target="_blank"
            rel="noopener noreferrer"
          >
            <EnvelopeIcon className="h-5 w-5 bg-transparent" />
          </a>

          <Button variant="secondary" size="md" onClick={handleHeadlineCopy}>
            {showCopied === "headline" ? "Copied!" : "Copy Headline"}
          </Button>

          {isValidDetail(headline.content) && (
            <Button variant="secondary" size="md" onClick={handleBodyCopy}>
              {showCopied === "body" ? "Copied!" : "Copy Body"}
            </Button>
          )}
          {/* Gated on the subject, not the payload's `slug`: the URL is
              derived from the subject, and the stored slug uses a different
              rule to the public site's. */}
          {internalNewsquawkUser && Boolean(headline.subject) && (
            <Button variant="secondary" size="md" onClick={handleUrlCopy}>
              {showCopied === "url" ? "Copied!" : "Copy Public URL"}
            </Button>
          )}
        </div>

        {/* Tags section */}
        {(displayTags.length > 0 || tagsLoading) && (
          <div className="space-y-1 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <strong className="text-gray-900 dark:text-gray-100">Tags</strong>
            {tagsLoading ? (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-gray-500 dark:text-gray-400">
                  Loading tags...
                </span>
              </div>
            ) : (
              <ShowMoreWrapper
                maxHeight="3rem"
                gradientFrom="transparent"
                gradientTo="rgb(243 244 246)"
                gradientToDark="rgb(31 41 55)"
                showMoreText="Show more tags"
                showLessText="Show less"
              >
                <div className="flex flex-wrap gap-2 mt-2">
                  {displayTags.map((tag) => (
                    <span
                      key={tag.tag_id}
                      className="px-2 py-1 rounded text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    >
                      {tag.name || "[Tag label not available]"}
                    </span>
                  ))}
                </div>
              </ShowMoreWrapper>
            )}
          </div>
        )}

        {children}
      </div>
    </>
  );
};

const isValidDetail = (detail: string | null) =>
  detail && detail.trim().length > 0 && detail !== "<!---->";

/** Remove standalone lines from content that duplicate the source attribution. */
const stripSourceFromContent = (
  content: string | null | undefined,
  source: string | null | undefined
): string => {
  if (!content || !source) return content || "";
  const plainSource = source.replace(/<[^>]*>/g, "").trim();
  if (!plainSource) return content;
  const sourceLower = plainSource.toLowerCase();
  return content
    .split("\n")
    .filter((line) => {
      const stripped = line
        .trim()
        .replace(/\*+/g, "")
        .replace(/_+/g, "")
        .trim();
      return stripped.toLowerCase() !== sourceLower;
    })
    .join("\n");
};

// Override for react-markdown to ensure all links open in a new tab
const markdownComponents: Components = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  a: ({ node, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
};

/** Ensure any <a> tags in raw HTML open in a new tab */
const ensureLinksOpenNewTab = (html: string): string =>
  html.replace(/<a\s/g, '<a target="_blank" rel="noopener noreferrer" ');

const createMailtoLink = (subject: string, plainContent: string) => {
  const mailSubject = encodeURIComponent(subject);

  // Convert each HTML field to plain text
  // Construct the email body with clear formatting
  const body = encodeURIComponent(
    `
Hey,

This just landed on my desk from Newsquawk - thought you should see it:

${subject}

${plainContent}

I can hop on the phone and discuss.

Thanks!
            `.trim()
  );

  return `mailto:?subject=${mailSubject}&body=${body}`;
};

export default NewsquawkHeadlineItem;
